import React, { useCallback, useEffect, useMemo, useState, lazy, Suspense } from 'react';
import SourceDetailsHeader from '@components/sources/source-view/source-details-header';
import { observer } from 'mobx-react';
import CloudSourcesInfo from '@stores/cloudSourcesInfo/cloudSourcesInfo';
import CloudSourceDetails from '@components/sources/sourceDetails/cloudSourceDetails';
import SourceSchema from '@components/sources/source-view/source-schema';
import SourceConfigureData from '@components/sources/source-view/source-configure-data';
import SourceResources from '@components/sources/source-view/source-resources';
import SourceEvents from '@components/sources/source-view/source-events';
import { useOpenLiveEvents } from '@components/common/liveEvents/hooks';
import useStores from '@stores/useStores';
import SourceFactory from '@components/sources/sourceFactory';
import { RouteComponentProps } from 'react-router-dom';
import useOnboarding from '@components/common/onboarding/hooks';
import isFeatureEnabled, { Feature } from '@utils/enableFeature';
import SourceAuditLogs from './components/audit-logs';
import classes from './source-details.module.scss';
import { disabledLiveEventMsg, isSampleSource, validateSourceName } from './utils';
import { TabsV1 } from '@ui-library/tabs';
import SourceTrackingPlan from './source-tracking-plan';
import { Skeleton } from 'antd';

const Permissions = lazy(() => import('@components/common/permissions'));
const SourceOverview = lazy(() => import('@components/sources/source-view/source-overview'));
const SourceSettings = lazy(() => import('@components/sources/source-view/source-settings'));

export const TAB_NAMES = {
  DESTINATIONS: 'Overview',
  SYNCS: 'Syncs',
  SCHEMA: 'Schema',
  SETTINGS: 'Settings',
  EVENTS: 'Events',
  AUDIT_LOGS: 'Audit Logs',
  RESOURCES: 'Resources',
  CONFIGURATION: 'Configuration',
  TRACKING_PLANS: 'Tracking Plans',
  PERMISSIONS: 'Permissions',
};

const SourceView = (props: RouteComponentProps<{ id: string }>) => {
  const {
    sourcesListStore,
    sourceDataStore: source,
    userStore,
    organizationStore,
    workspaceStore,
    permissionsStore,
  } = useStores();
  const { isLiveEventTourPending, markLiveEventComplete } = useOnboarding(userStore);
  const [isTourPending] = useState(isLiveEventTourPending);
  const [tab, setActiveTab] = useState('');
  const isEnabled = isFeatureEnabled();
  const showAuditLog = isEnabled(Feature.auditLogs);

  const tabs = useMemo(() => {
    let tabs: string[] = [];
    if (source?.sourceDef) {
      tabs = SourceFactory(source.sourceDef.category).getTabs(source);
      if (showAuditLog) {
        tabs = [...tabs, TAB_NAMES.AUDIT_LOGS];
      }
      if (isEnabled(Feature.trackingPlans)) {
        tabs = [...tabs, TAB_NAMES.TRACKING_PLANS];
      }
      if (isEnabled(Feature.permissions)) {
        tabs = [...tabs, TAB_NAMES.PERMISSIONS];
      }
    }
    return tabs;
  }, [source?.sourceDef, showAuditLog]);

  const sourceId = props.match.params.id;

  const currentSource = sourcesListStore?.sourceById(sourceId);

  useEffect(() => {
    if (isSampleSource(source)) {
      markLiveEventComplete();
    }
  }, [source?.config?.isSampleSource]);

  useEffect(() => {
    source!.reset();
    if (currentSource) {
      source!.setSource(currentSource!);
    } else {
      props.history.push('/sources');
    }
  }, [sourceId, sourcesListStore]);

  useEffect(() => {
    const searchParams = new URLSearchParams(props.location.search);
    const tabName = searchParams.get('tab') || '';
    if (tabName && tabs.includes(tabName)) {
      setActiveTab(tabName);
    } else {
      setActiveTab(TAB_NAMES.DESTINATIONS);
    }
  }, [props.location.search, tabs]);

  const { onClick: onClickLiveEvents } = useOpenLiveEvents(
    organizationStore,
    'sources',
    source?.id || '',
  );

  const onClickTab = useCallback(
    (name: string) => {
      setActiveTab(name);
      props.history.push(`/sources/${source!.id}?tab=${name}`);
    },
    [source?.id, props.history],
  );

  if (!source?.id) {
    return <div>Loading</div>;
  }

  const hasSourcePermission = permissionsStore.hasPermission(source.id);

  const tabContent = (
    <>
      {tab === TAB_NAMES.DESTINATIONS ? (
        <Suspense fallback={<Skeleton loading />}>
          <SourceOverview />
        </Suspense>
      ) : null}
      {tab === TAB_NAMES.EVENTS ? <SourceEvents source={currentSource!} /> : null}
      {tab === TAB_NAMES.SYNCS ? (
        <CloudSourcesInfo source={source!} destinations={source?.destinations}>
          <CloudSourceDetails isAdmin={hasSourcePermission} />
        </CloudSourcesInfo>
      ) : null}
      {tab === TAB_NAMES.SCHEMA ? <SourceSchema isAdmin={hasSourcePermission} /> : null}
      {tab === TAB_NAMES.CONFIGURATION ? (
        <SourceConfigureData isAdmin={hasSourcePermission} />
      ) : null}
      {tab === TAB_NAMES.RESOURCES ? <SourceResources isAdmin={hasSourcePermission} /> : null}
      {tab === TAB_NAMES.SETTINGS ? (
        <Suspense fallback={<Skeleton loading />}>
          <SourceSettings
            sourceDataStore={source}
            sourcesListStore={sourcesListStore}
            isAdmin={hasSourcePermission}
            history={props.history}
            dataPlaneURL={workspaceStore.dataPlane}
            isEnterprise={organizationStore.isEnterprise}
          />
        </Suspense>
      ) : null}
      {tab === TAB_NAMES.AUDIT_LOGS ? <SourceAuditLogs targetId={source!.id} /> : null}
      {tab === TAB_NAMES.TRACKING_PLANS ? (
        <SourceTrackingPlan source={source} isAdmin={hasSourcePermission} />
      ) : null}
      {tab === TAB_NAMES.PERMISSIONS && (
        <Suspense fallback={<Skeleton loading />}>
          <Permissions resourceId={source.id} resourceType="source" />
        </Suspense>
      )}
    </>
  );

  return (
    <div className={classes.source_details_container} data-testid="source-view">
      <SourceDetailsHeader
        backButton={{ label: 'All Sources', linkTo: '/sources' }}
        name={source.name}
        onClickLiveEvents={onClickLiveEvents}
        defName={source.sourceDef?.name}
        handleNameSave={source.updateName}
        showCopyIcon={false}
        disabledLiveEventMsg={disabledLiveEventMsg(
          source.enabled,
          !!permissionsStore.dataPermissions?.locked,
        )}
        isAdmin={hasSourcePermission}
        validateInput={validateSourceName}
        displayName={source.sourceDef?.displayName}
        showOnboarding={isSampleSource(source) && isTourPending}
      />
      <TabsV1
        onChange={onClickTab}
        items={tabs.map((t) => ({
          title: t,
          key: t,
        }))}
        content={tabContent}
        activeKey={tab}
      />
    </div>
  );
};

export default observer(SourceView);
